import { eq, useLiveQuery } from '@tanstack/react-db'
import { useState } from 'react'
import { applyAddendums, orderAddendums } from '@/db/application/addendums'
import { activeLogs } from '@/db/application/callouts'

import { getAllowedSessionStatusTransitions } from '@/db/domain/sessionStatus'
import type { CalloutDetailsSavePayload, CalloutDetailsScreenProps } from '@/screens/CalloutDetails.types'
import { useDb } from '@/lib/dbContext'
import type { Addendum, AddendumChange, Callout, Customer, Note, ServiceRevision, SessionSubtype, StatusChangeLog } from '@/lib/schemas/db'
import { withSpan } from '@/lib/sentry'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'

type DetailsState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | { kind: 'ready'; screenProps: CalloutDetailsScreenProps }

function logsToNotes(
  logs: Note[] | undefined,
  getAuthorDisplayName: (authorId: string) => string,
) {
  // Soft-deleted notes are no longer part of the record; the audit trail for
  // them lives on the Log Work feed.
  const visible = activeLogs(logs ?? [])
  if (visible.length === 0) return []

  return visible.map((log) => {
    if (log.photoUrl) {
      return {
        id: log.id,
        kind: 'photo' as const,
        authorName: getAuthorDisplayName(log.authorId),
        createdAt: log.createdAt,
        photoUrl: log.photoUrl,
        photoUrls: log.photoUrls,
        caption: log.caption ?? '',
      }
    }

    return {
      id: log.id,
      kind: 'text' as const,
      authorName: getAuthorDisplayName(log.authorId),
      createdAt: log.createdAt,
      text: log.text ?? '',
    }
  })
}

export function useSessionDetails(sessionIdentity: string): DetailsState {
  const db = useDb()
  const [amending, setAmending] = useState(false)
  const tryPublicId = looksLikePublicId(sessionIdentity)

  const sessionByIdResult = useLiveQuery(
    (q) =>
      q
        .from({ sessions: db.sessionsCollection })
        .where(({ sessions }) => eq(sessions.id, sessionIdentity))
        .findOne(),
    [sessionIdentity],
  )

  const sessionByPublicIdResult = useLiveQuery(
    (q) =>
      tryPublicId
        ? q
            .from({ sessions: db.sessionsCollection })
            .where(({ sessions }) => eq(sessions.publicId, sessionIdentity))
            .findOne()
        : null,
    [sessionIdentity, tryPublicId],
  )

  const session =
    (sessionByIdResult.data as Callout | undefined) ??
    (sessionByPublicIdResult.data as Callout | undefined)
  const sessionId = session?.id ?? sessionIdentity
  if (!tryPublicId && session && (sessionByIdResult.data as Callout | undefined)) {
    captureLegacyIdRoute('session', sessionIdentity)
  }

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))

  const notesResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ notes: db.sessionNotesCollection })
            .where(({ notes }) => eq(notes.serviceId, sessionId))
            .orderBy(({ notes }) => notes.createdAt)
        : null,
    [sessionId],
  )

  const addendumsResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ addendums: db.sessionAddendumsCollection })
            .where(({ addendums }) => eq(addendums.serviceId, sessionId))
            .orderBy(({ addendums }) => addendums.createdAt)
        : null,
    [sessionId],
  )

  const revisionsResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ revisions: db.sessionRevisionsCollection })
            .where(({ revisions }) => eq(revisions.serviceId, sessionId))
            .orderBy(({ revisions }) => revisions.createdAt)
        : null,
    [sessionId],
  )

  const statusChangesResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ statusChanges: db.sessionStatusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, sessionId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [sessionId],
  )

  const customerResult = useLiveQuery(
    (q) => {
      const customerId = session?.customerId
      if (!customerId) return null

      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, customerId))
        .findOne()
    },
    [session?.customerId],
  )

  // Archived sessions aren't in the live sync; pull one in on demand.
  const recordQueriesSettled =
    !sessionByIdResult.isLoading &&
    !sessionByPublicIdResult.isLoading &&
    !sessionByIdResult.isError &&
    !sessionByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'session',
    sessionIdentity,
    recordQueriesSettled && !session,
  )

  if (
    sessionByIdResult.isLoading ||
    sessionByPublicIdResult.isLoading ||
    usersResult.isLoading ||
    notesResult.isLoading ||
    statusChangesResult.isLoading ||
    hydrating
  ) {
    return { kind: 'loading' }
  }

  if (
    sessionByIdResult.isError ||
    sessionByPublicIdResult.isError ||
    usersResult.isError ||
    notesResult.isError ||
    statusChangesResult.isError
  ) {
    return { kind: 'error' }
  }

  if (!session) {
    return { kind: 'not_found' }
  }

  const customer = customerResult.data as Customer | undefined
  const customerName = customer
    ? customer.externalId
      ? `${customer.fullName} – ${customer.externalId}`
      : customer.fullName
    : session.contactName ?? 'Customer'

  const noteList = logsToNotes(
    notesResult.data as Note[] | undefined,
    db.getAuthorDisplayName,
  )

  const statusTransitions = getAllowedSessionStatusTransitions(session.status)

  const handleSaveDetails = ({
    contactAddress,
    contactEmail,
    contactName,
    contactPhone,
    invoiceAddress,
    invoiceEmail,
    invoiceId,
    invoiceName,
    invoicePhone,
    invoiceVat,
    issueSummary,
    startTime,
    endTime,
    isWholeDay,
  }: CalloutDetailsSavePayload) => {
    db.sessionsCollection.update(session.id, (draft) => {
      draft.contactAddress = contactAddress
      draft.contactName = contactName
      draft.contactPhone = contactPhone
      draft.contactEmail = contactEmail
      draft.invoiceName = invoiceName
      draft.invoiceAddress = invoiceAddress
      draft.invoiceEmail = invoiceEmail
      draft.invoicePhone = invoicePhone
      draft.invoiceVat = invoiceVat
      draft.invoiceId = invoiceId
      draft.issueSummary = issueSummary
      draft.startTime = startTime
      draft.endTime = endTime
      draft.isWholeDay = isWholeDay
      draft.updatedAt = new Date().toISOString()
    })
  }

  const handleChangeCustomer = (customerId: string) => {
    db.sessionsCollection.update(session.id, (draft) => {
      draft.customerId = customerId
      draft.updatedAt = new Date().toISOString()
    })
  }

  const handleChangeStatus = async (status: Callout['status'], reason?: string) =>
    withSpan(
      {
        op: 'remote-session.status.change',
        name: 'Change session status (details)',
        attributes: { sessionId: session.id, nextStatus: status },
      },
      () =>
        db.updateSessionAndWait(session.id, {
          status,
          statusReason:
            status === 'Review' || status === 'Cancelled'
              ? reason?.trim() || `Changed from session details to ${status}`
              : reason?.trim() || undefined,
        }),
    )

  const handleAddNote = (text: string) => {
    if (!text.trim()) return false
    db.appendSessionLog(session.id, { text })
    return true
  }

  const handleUpdateNote = (
    id: string,
    patch: { text?: string; caption?: string },
  ) => {
    db.updateSessionLog(session.id, id, patch)
  }

  const handleDeleteNote = (id: string) => {
    db.deleteSessionLog(session.id, id)
  }

  const handleChangeSessionSubtype = async (subtype: SessionSubtype) => {
    await db.sessionsCollection
      .update(session.id, (draft) => {
        draft.sessionSubtype = subtype
        draft.updatedAt = new Date().toISOString()
      })
      .isPersisted.promise
  }

  const addendums = orderAddendums(
    (addendumsResult.data as Addendum[] | undefined) ?? [],
  )

  const effective = applyAddendums(
    session as unknown as Record<string, unknown>,
    addendums,
  ) as unknown as typeof session

  const handleSaveAddendum = async (
    reason: string,
    changes: AddendumChange[],
  ) => {
    await db.appendSessionAddendumAndWait(session.id, { reason, changes })
    setAmending(false)
  }

  return {
    kind: 'ready',
    screenProps: {
      serviceId: session.id,
      publicId: session.publicId,
      customerId: session.customerId,
      customerPublicId: customer?.publicId,
      customerName,
      serviceTypeLabel: session.sessionSubtype ?? 'TeamViewer Session',
      sessionSubtype: session.sessionSubtype ?? 'TeamViewer Session',
      linkedCustomer: customer
        ? {
            fullName: customer.fullName,
            externalId: customer.externalId,
            contactEmail: customer.contactEmail,
            contactPhone: customer.contactPhone,
            contactAddress: customer.contactAddress,
            invoiceName: customer.invoiceName,
            invoiceEmail: customer.invoiceEmail,
            invoicePhone: customer.invoicePhone,
            invoiceAddress: customer.invoiceAddress,
            invoiceVat: customer.invoiceVat,
          }
        : undefined,
      status: session.status,
      statusTransitions,
      contactAddress: effective.contactAddress,
      contactName: effective.contactName,
      contactPhone: effective.contactPhone,
      contactEmail: effective.contactEmail,
      invoiceName: effective.invoiceName,
      invoiceAddress: effective.invoiceAddress,
      invoiceEmail: effective.invoiceEmail,
      invoicePhone: effective.invoicePhone,
      invoiceVat: effective.invoiceVat,
      invoiceId: effective.invoiceId,
      issueSummary: effective.issueSummary,
      startTime: session.startTime,
      endTime: session.endTime,
      isWholeDay: session.isWholeDay,
      notes: noteList,
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      getAuthorDisplayName: db.getAuthorDisplayName,
      onSaveDetails: handleSaveDetails,
      onChangeStatus: handleChangeStatus,
      onChangeCustomer: handleChangeCustomer,
      onAddNote: handleAddNote,
      onUpdateNote: handleUpdateNote,
      onDeleteNote: handleDeleteNote,
      onChangeSessionSubtype: handleChangeSessionSubtype,
      addendums,
      revisions: (revisionsResult.data as ServiceRevision[] | undefined) ?? [],
      lockedValues: {
        issueSummary: session.issueSummary,
        contactAddress: session.contactAddress,
        contactName: session.contactName,
        contactPhone: session.contactPhone,
        contactEmail: session.contactEmail,
        invoiceName: session.invoiceName,
        invoiceEmail: session.invoiceEmail,
        invoicePhone: session.invoicePhone,
        invoiceAddress: session.invoiceAddress,
        invoiceVat: session.invoiceVat,
        invoiceId: session.invoiceId,
      },
      amending,
      onStartAmend: () => setAmending(true),
      onDiscardAmend: () => setAmending(false),
      onSaveAddendum: handleSaveAddendum,
    },
  }
}
